import React, { useContext, useEffect, useRef } from 'react';
import { merge } from 'lodash';
import {
  createGlobalStyle,
  ThemeContext,
  ThemeProvider as SCThemeProvider,
} from 'styled-components';
import '@rsos/theme/theme.scss';
import './fontStyles.scss';
import { getTheme } from './themes.js';

/**
 * Get the theme name of the currently active theme.
 * @returns {string} themeName
 */
export const useThemeName = () => {
  const { colors = { name: 'light' } } = useContext(ThemeContext);
  return colors.name;
};

const GlobalStyle = createGlobalStyle`
  html,
  body {
    text-rendering: optimizelegibility;
    margin: 0;
    padding: 0;
    overflow: auto;
    overscroll-behavior-y: none; /* Prevents vertical bouncing */
  }
  * {
    font-family: 'Roboto', sans-serif;
   }
  /* Make sure box-sizing is consistent across browsers */
  html {
    box-sizing: border-box;
  }
  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }
`;

/**
 * This Redux disconnected ThemeProvider, accepts theme as a prop
 * @param {React.Children} props.children
 */
const ThemeProvider = ({
  themeName,
  children,
  themeOverrides,
  injectGlobalStyles = true,
}) => {
  const theme = merge({}, getTheme(themeName), themeOverrides);
  const oldThemeName = useRef(themeName);
  const parentTheme = useContext(ThemeContext);

  // Adds a class to `body` for theming via CSS custom properties.
  // If you're looking at this because you want a *nested* theme --
  // for an inverse-colored modal or something -- you'll need to add
  // a <div> with the theme class. There are too many different apps
  // using this provider to put a layout-changing <div> here in the
  // global provider.
  useEffect(() => {
    // In tests, `document` may not be defined.
    if (!parentTheme && typeof document !== 'undefined' && document) {
      document.body.classList.remove(`rapidsos-${oldThemeName.current}-theme`);
      document.body.classList.add(`rapidsos-${themeName}-theme`);
      oldThemeName.current = themeName;
    }
  }, [themeName, parentTheme]);

  return (
    <SCThemeProvider theme={theme}>
      {injectGlobalStyles && <GlobalStyle />}
      {children}
    </SCThemeProvider>
  );
};

export default ThemeProvider;
